import { ActionType, DateOnly, getListApiHttpParam, IListState, mapToListModel, objectToFormData } from '@aston/foundation';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { UsersListSortProp, IUsersList, ISubsidiaryUser, ISubsidiaryUserRole, ISubsidiaryUserStatus, ISubsidiaryAppliedUserDelegations, ISubsidiaryFormUserDelegations, ISubsidiaryFormUserInactivity } from '@aston/middle-users-admin';

import { mapToSubsidiaryDetailModel, mapToSubsidiaryUpdateFormApi, mapToSubsidiaryFromLegalIdentifierModel, mapToSubsidiaryListItemModel, mapToUserDelegationsApiModel, mapToUserDelegationsFormModel, mapToUserDelegationsModel, mapToSubsidiaryCreateFormApi, mapToSubsidiaryBlockedFormApi, mapToUserInactivityApiModel } from '../functions/mappers.function';
import { ISubsidiaryListItemApi, ISubsidiaryFromLegalIdentifierApi, ISubsidiaryDetailApi, IUsersListApi, ISubsidiaryUserApi, ISubsidiaryAppliedUserDelegationsApi, ISubsidiaryFormUserDelegationsApi } from '../models/api';
import { ISubsidiaryListItem, ISubsidiaryBlockedForm, ISubsidiaryForm, ISubsidiaryFromLegalIdentifier, ISubsidiaryDetail } from '../models';
import { mapToUserItemModel } from '../functions/users.mappers.function';
import { AppStoreSelectors } from '../../root-store/app-store';
import { TenantUrl } from '../../shared-module/functions';

@Injectable({
	providedIn: 'root'
})
export class SubsidiariesService {

	private http = inject(HttpClient)
	private store = inject(Store)

	getActiveSubsidiaries(): Observable<ISubsidiaryListItem[]> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<ISubsidiaryListItemApi[]>(
				`${config.apiUrl}/api/Subsidiaries?isInitializedOnlySubsidiary=true`).pipe(
				map(apiModel => apiModel.map(m => mapToSubsidiaryListItemModel(m)))
			)),
		);
	}

	getAllSubsidiaries(): Observable<ISubsidiaryListItem[]> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<ISubsidiaryListItemApi[]>(
				`${config.apiUrl}/api/Subsidiaries?isInitializedOnlySubsidiary=false`).pipe(
				map(apiModel => apiModel.map(m => mapToSubsidiaryListItemModel(m)))
			)),
		);
	}

	createSubsidiary(model: ISubsidiaryForm) {
		const formData = objectToFormData(mapToSubsidiaryCreateFormApi(model));
		const headers = new HttpHeaders({ enctype: 'multipart/form-data' });

		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post<number>(`${config.apiUrl}/api/Subsidiaries`, formData, { headers }).pipe(
				map(id => ({ ...model, id }))
			)),
		);
	}

	createSubsidiaryFailure(model: ISubsidiaryBlockedForm) {
		const params = mapToSubsidiaryBlockedFormApi(model);

		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post(`${config.apiUrl}/api/Subsidiaries/failure`, params)),
		);
	}

	getSubsidiaryFromLegalIdentifier(legalIdentifier: string): Observable<ISubsidiaryFromLegalIdentifier> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<ISubsidiaryFromLegalIdentifierApi>(
				`${config.apiUrl}/api/Subsidiaries/${legalIdentifier}/check`).pipe(
				map(apiModel => mapToSubsidiaryFromLegalIdentifierModel(apiModel))
			))
		);
	}

	getSubsidiary(id: number): Observable<ISubsidiaryDetail> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<ISubsidiaryDetailApi>(`${config.apiUrl}/api/Subsidiaries/${id}`).pipe(
				map(apiModel => mapToSubsidiaryDetailModel(apiModel))
			)),
		);
	}

	getTenantUrl(subsidiaryId: number): Observable<TenantUrl> {
		const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get(`${config.apiUrl}/api/Subsidiaries/${subsidiaryId}/users/me/tenant-url`, {
				headers, responseType: 'json'
			})),
			map(url => new TenantUrl(url as string))
		);
	}

	updateSubsidiary(subsidiaryId: number, model: ISubsidiaryForm) {
		const formData = objectToFormData(mapToSubsidiaryUpdateFormApi(model));
		const headers = new HttpHeaders({ enctype: 'multipart/form-data' });

		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post(`${config.apiUrl}/api/Subsidiaries/${subsidiaryId}`, formData, { headers })),
		);
	}

	getSubsidiaryUsers(id: number, params: IListState<UsersListSortProp>): Observable<IUsersList> {
		const httpParams = getListApiHttpParam(params);
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<IUsersListApi>(`${config.apiUrl}/api/Subsidiaries/${id}/Users`, {params: httpParams}).pipe(
				map(apiModel => {
					return mapToListModel<ISubsidiaryUserApi, ISubsidiaryUser>(apiModel, mapToUserItemModel) as IUsersList;
				})
			))
		);
	}

	getSubsidiaryRoles(subsidiaryId: number): Observable<string[]> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<string[]>(`${config.apiUrl}/api/Subsidiaries/${subsidiaryId}/Roles`)
			));
	}

	updateSubsidiaryRole(subsidiaryId: number, model: ISubsidiaryUserRole) {
		const params = {
			subsidiaryRole: model.subsidiaryRole
		};
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post(`${config.apiUrl}/api/Subsidiaries/${subsidiaryId}/Users/${model.userId}/role`, params)),
		);
	}

	resendInvitationToUser(subsidiaryId: number, userId: number) {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post(`${config.apiUrl}/api/Subsidiaries/${subsidiaryId}/Users/${userId}/resend-invitation`, null)),
		);
	}

	updateUserStatus(subsidiaryId: number, model: ISubsidiaryUserStatus) {
		const params = {
			status: model.userStatus
		};
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post(`${config.apiUrl}/api/Subsidiaries/${subsidiaryId}/Users/${model.userId}/status`, params)),
		);
	}

	postSubsidiaryUser(subsidiaryId: number, model: ISubsidiaryUser) {
		const params = model;
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post(`${config.apiUrl}/api/Subsidiaries/${subsidiaryId}/users`, params)),
		);
	}

	getUserDelegationsOnSubsidiary(subsidiaryId: number, userId: number): Observable<ISubsidiaryAppliedUserDelegations> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<ISubsidiaryAppliedUserDelegationsApi>(`${config.apiUrl}/api/Subsidiaries/${subsidiaryId}/users/${userId}/delegations/current`)),
			map(apiModel => mapToUserDelegationsModel(apiModel))
		);
	}

	getUserDelegationsFormOnSubsidiary(userId: number, subsidiaryId: number): Observable<ISubsidiaryFormUserDelegations> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<ISubsidiaryFormUserDelegationsApi>(`${config.apiUrl}/api/Subsidiaries/${subsidiaryId}/users/${userId}/delegation`)),
			map(apiModel => mapToUserDelegationsFormModel(apiModel, (apiModel.userRoleDelegationIds?.length || apiModel.superDebtorDelegationIds?.length) ? ActionType.UPDATE : ActionType.CREATE, userId))
		);
	}

	createUserDelegationsOnSubsidiary(userId: number, subsidiaryId: number, delegations: ISubsidiaryFormUserDelegations) {
		const apiModel = mapToUserDelegationsApiModel(delegations);
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post(`${config.apiUrl}/api/Subsidiaries/${subsidiaryId}/users/${userId}/delegation`, apiModel)),
		);
	}

	updateUserInactivityOnSubsidiary(userId: number, subsidiaryId: number, inactivity: ISubsidiaryFormUserInactivity) {
		const apiModel = mapToUserInactivityApiModel(inactivity);
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post(`${config.apiUrl}/api/Subsidiaries/${subsidiaryId}/users/${userId}/inactivity`, apiModel)),
		);
	}

	updateUserDelegationsOnSubsidiary(userId: number, subsidiaryId: number, delegations: ISubsidiaryFormUserDelegations) {
		const apiModel = mapToUserDelegationsApiModel(delegations);
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post(`${config.apiUrl}/api/Subsidiaries/${subsidiaryId}/users/${userId}/delegationUpdate`, apiModel)),
		);
	}

	updateDelegatedUsersDelegationsOnSubsidiary(userId: number, subsidiaryId: number) {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post(`${config.apiUrl}/api/Subsidiaries/${subsidiaryId}/users/${userId}/delegationUpdate`, {})),
		);
	}

	updateTenantTimeout(subsidiaryId: number, accessEndDate: DateOnly): Observable<object> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post(`${config.apiUrl}/api/Subsidiaries/${subsidiaryId}/AccessEndDate`, { accessEndDate }))
		);
	}
}
